import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Check, X, ExternalLink, Send } from "lucide-react";

const FILTERS = ["pending", "approved", "rejected", ""];

export const CopyrightTab = () => {
  const [status, setStatus] = useState("pending");
  const [claims, setClaims] = useState([]);
  const [notes, setNotes] = useState({});
  const load = () => api.get("/admin/copyright", { params: status ? { status } : {} }).then(r => setClaims(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (id, action) => {
    try { await api.post(`/admin/copyright/${id}/resolve`, { action, note: notes[id] || "" }); toast.success(action === "approve" ? "Video taken down, creator notified" : "Claim rejected"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-copyright">
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map(f => (
          <button key={f || "all"} onClick={() => setStatus(f)} data-testid={`copyright-filter-${f || "all"}`}
            className={`chip jx-btn px-4 h-9 rounded-full text-xs font-semibold capitalize ${status === f ? "chip-active" : ""}`}>{f || "all"}</button>
        ))}
      </div>
      <div className="space-y-3">
        {claims.length === 0 && <div className="p-5 rounded-2xl border border-white/10 text-sm text-white/50" data-testid="copyright-empty">No claims.</div>}
        {claims.map(c => (
          <div key={c.id} className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid={`copyright-claim-${c.id}`}>
            <div className="flex flex-wrap items-start gap-2 justify-between">
              <div className="min-w-0">
                <Link to={`/watch/${c.video_id}`} className="font-bold hover:text-[#00E7FF] inline-flex items-center gap-1">{c.video_title} <ExternalLink size={12}/></Link>
                <div className="text-xs text-white/50">by @{c.creator_username} · filed {new Date(c.created_at).toLocaleString()}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${c.status === "pending" ? "bg-[#FFD700]/15 text-[#FFD700]" : c.status === "approved" ? "bg-[#FF3EA5]/15 text-[#FF3EA5]" : "bg-white/10 text-white/60"}`} data-testid={`copyright-status-${c.id}`}>{c.status}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-3 text-xs text-white/70">
              <div><b className="text-white/90">Claimant:</b> {c.claimant_name}{c.organization ? ` (${c.organization})` : ""} · {c.email}</div>
              <div><b className="text-white/90">Signature:</b> {c.signature}</div>
              <div className="sm:col-span-2"><b className="text-white/90">Original work:</b> {c.original_work}</div>
              <div className="sm:col-span-2 whitespace-pre-wrap"><b className="text-white/90">Details:</b> {c.description}</div>
              {c.admin_note && <div className="sm:col-span-2"><b className="text-white/90">Admin note:</b> {c.admin_note}</div>}
            </div>
            {c.status === "pending" && (
              <div className="flex flex-wrap gap-2 mt-3">
                <input value={notes[c.id] || ""} onChange={e => setNotes(n => ({ ...n, [c.id]: e.target.value }))} placeholder="Note to creator (optional)"
                  data-testid={`copyright-note-${c.id}`} className="flex-1 min-w-[180px] h-9 rounded-full bg-white/5 border border-white/10 px-4 text-xs focus:outline-none focus:border-[#00E7FF]"/>
                <button onClick={() => resolve(c.id, "approve")} data-testid={`copyright-approve-${c.id}`} className="jx-btn h-9 px-4 rounded-full bg-[#FF3EA5] text-white text-xs font-bold inline-flex items-center gap-1"><Check size={13}/> Approve & take down</button>
                <button onClick={() => resolve(c.id, "reject")} data-testid={`copyright-reject-${c.id}`} className="jx-btn h-9 px-4 rounded-full border border-white/15 text-xs font-bold inline-flex items-center gap-1"><X size={13}/> Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const DigestTab = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/digest/run"); toast.success(`Digest sent to ${data.sent} followers`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="p-5 rounded-2xl border border-white/10 max-w-xl" data-testid="admin-digest">
      <h3 className="h-font text-xl font-bold">Weekly digest</h3>
      <p className="text-sm text-white/60 mt-1">Sent automatically every Sunday at 6 PM UTC. Each follower gets a push + in-app notification with the top new videos from creators they follow (last 7 days).</p>
      <button onClick={run} disabled={busy} data-testid="digest-run-btn" className="jx-btn mt-4 h-10 px-5 rounded-full bg-[#00E7FF] text-black font-extrabold text-sm inline-flex items-center gap-2"><Send size={14}/> {busy ? "Sending…" : "Send digest now"}</button>
    </div>
  );
};

export const TvAppsTab = () => (
  <div className="p-5 rounded-2xl border border-white/10 max-w-2xl space-y-3 text-sm text-white/70" data-testid="admin-tvapps">
    <h3 className="h-font text-xl font-bold text-white">TV apps</h3>
    <p><b className="text-white">Fire TV / Android TV / smart TVs:</b> open <Link to="/tv" className="text-[#00E7FF]" data-testid="tvapps-open-tv">{window.location.origin}/tv</Link> — remote-friendly big-screen app. Submit this URL as an Amazon Appstore Web App to publish on Fire TV.</p>
    <p><b className="text-white">Roku:</b> download the channel source, set <code>jixxeo_feed_url</code> in <code>manifest</code> to your live domain, sideload via Developer Mode, then publish from developer.roku.com.</p>
    <a href="/downloads/jixxeo-roku.zip" download data-testid="tvapps-roku-download" className="jx-btn inline-flex h-10 px-5 rounded-full bg-[#00E7FF] text-black font-extrabold items-center">Download Roku channel (.zip)</a>
    <p className="text-xs text-white/40">Feed: <code>/api/tv/feed</code> (free, public videos only).</p>
  </div>
);
